import { jwtDecode } from "jwt-decode";
import { chain } from "lodash";
import type { GlobalAccessType } from "../services/workspaces/global.access";
import type {
  RoleUser,
  WorkspaceRole,
  WorkspaceRoles,
} from "../state/workspaces/types";
import { Localized } from "../strings";
import { ShareType } from "../ui/modals/sharing/ShareType";
import type { UserFullPayload } from "./credential.helpers";

export function calculatePermissions(globalAccess: string) {
  let publicPermissionLevel: ShareOptionValue;
  if (globalAccess === "WRITE") {
    publicPermissionLevel = ShareType.EDIT;
  } else if (globalAccess === "READ") {
    publicPermissionLevel = ShareType.READ;
  } else {
    publicPermissionLevel = ShareType.NO_ACCESS;
  }
  return publicPermissionLevel;
}

export function formatWorkspaceId(workspaceId: string) {
  let formattedId: string = "";
  for (let i = 1; i <= workspaceId.length; i++) {
    formattedId += workspaceId[i - 1];
    if (i % 3 === 0 && i !== workspaceId.length) {
      formattedId += " ";
    }
  }
  return formattedId;
}

export const sanitizeUrl = (string: string): string => {
  const stringUrl = string.trim();
  try {
    const url = new URL(stringUrl);
    if (url.protocol === "http:" || url.protocol === "https:") {
      // When string is a valid url - remove www. prefix from hostname
      return url.hostname.replace("www.", "");
    }
  } catch {
    // When string is not a valid url - remove www. prefix from string
    return string.replace("www.", "");
  }
  return string;
};

export const isOrganizationAdmin = (token: string) => {
  if (!token) return false;

  const userFullPayload = jwtDecode<UserFullPayload>(token);
  return !!userFullPayload.Permissions.Administer[0];
};

export function sendEmail(
  workspaceName: string,
  shareLink: string,
  recipients?: string[],
) {
  workspaceName = sanitizeUrl(workspaceName);
  const strings = Localized.object("COPY_EMAIL_LINK");
  let mailTo = "";
  if (recipients) {
    recipients.forEach((recipient, index) => {
      mailTo += recipient;
      if (recipients.length - 1 !== index) {
        mailTo += ";";
      }
    });
  }
  const emailLink =
    `mailto:${mailTo}?subject=${
      strings.WORKSPACE_SHARED_WITH_YOU
    } ${encodeURIComponent(workspaceName)}&` +
    `body=${strings.WORKSPACE_SHARED_WITH_YOU}%0D${encodeURIComponent(
      workspaceName,
    )}%0D${shareLink}%0D%0D${strings.LEARN_MORE}`;
  window.open(emailLink, "_blank");
}

export const getShareOptionFromGlobalAccess = (
  globalAccess: GlobalAccessType,
): ShareOptionValue => {
  const shareOptionValueMap: Record<GlobalAccessType, ShareOptionValue> = {
    NONE: ShareType.NO_ACCESS,
    READ: ShareType.READ,
    WRITE: ShareType.EDIT,
  };
  return shareOptionValueMap[globalAccess];
};

export const getGlobalAccessFromShareOption = (
  shareOptionValue: ShareOptionValue,
): GlobalAccessType => {
  const globalAccessMap: Record<number, GlobalAccessType> = {
    [ShareType.EDIT.value]: "WRITE",
    [ShareType.READ.value]: "READ",
    [ShareType.NO_ACCESS.value]: "NONE",
  };
  return globalAccessMap[shareOptionValue.value] ?? "NONE";
};

export const getEffectiveShareOption = (
  explicit: ShareOptionValue,
  globalAccess: GlobalAccessType,
): ShareOptionValue => {
  const floor = getShareOptionFromGlobalAccess(globalAccess);
  return explicit.value >= floor.value ? explicit : floor;
};

export const getShareOptionFromWorkspaceRole = (
  workspaceRole: WorkspaceRole,
) => {
  const shareOptionValueMap: Record<WorkspaceRole, ShareOptionValue> = {
    // administer: ShareType.MOD,
    administer: ShareType.ADMIN,
    none: ShareType.NO_ACCESS,
    read: ShareType.READ,
    write: ShareType.EDIT,
  };
  return shareOptionValueMap[workspaceRole];
};

export const getWorkspaceRoleFromShareOption = (
  shareOptionValue: ShareOptionValue,
) => {
  const roleAccessMap: Record<number, WorkspaceRole> = {
    [ShareType.ADMIN.value]: "administer",
    [ShareType.NO_ACCESS.value]: "none",
    [ShareType.READ.value]: "read",
    [ShareType.EDIT.value]: "write",
  };
  return roleAccessMap[shareOptionValue.value] ?? "none";
};

type RoleUserWithEmail = MarkNonNullable<RoleUser, "email">;

export type RoleUserPermission = RoleUserWithEmail & {
  currentRole: WorkspaceRole;
};

const isUserRoleWithEmail = (
  roleUser: RoleUser,
): roleUser is RoleUserWithEmail => !!roleUser.email?.trim();

const withCurrentRole =
  (currentRole: WorkspaceRole) =>
  (user: RoleUserWithEmail): RoleUserPermission => ({
    ...user,
    currentRole,
  });

type RoleUsers_WorkspaceRole_Tuple = [RoleUser[], WorkspaceRole];

const mapUsersWithCurrentRole = ([
  users,
  role,
]: RoleUsers_WorkspaceRole_Tuple): RoleUserPermission[] =>
  users.filter(isUserRoleWithEmail).map(withCurrentRole(role));

export const getWorkspaceRoles = (
  workspaceRoles: WorkspaceRoles,
): RoleUserPermission[] =>
  chain<RoleUsers_WorkspaceRole_Tuple>([
    [workspaceRoles.administrators, "administer"],
    [workspaceRoles.writers, "write"],
    [workspaceRoles.readers, "read"],
    [workspaceRoles.unspecifiedUsers, "none"],
  ])
    .flatMap(mapUsersWithCurrentRole)
    .uniqBy("email")
    .value();
